import { useState, useMemo, useRef, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { Box, CssBaseline, IconButton, Typography, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, Button } from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import UsbOffIcon from '@mui/icons-material/UsbOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import MenuIcon from '@mui/icons-material/Menu';
import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import { getTheme } from './theme';
import { DockviewReact, DockviewReadyEvent, DockviewApi, IDockviewHeaderActionsProps, DockviewGroupPanel, SerializedDockview } from 'dockview';
import 'dockview/dist/styles/dockview.css';
import './dockview-custom.css';

import { FirmwareUpdate } from './components/FirmwareUpdate';
import { PCRSettings } from './components/PCRSettings';
import { LogDisplay } from './components/LogDisplay';
import { CurrentProtocolPlot } from './components/CurrentProtocolPlot';
import { TemperaturePlot } from './components/TemperaturePlot';
import { HardwareMonitorPlot } from './components/HardwareMonitorPlot';
import { SerialIO } from './components/SerialIO';
import { Documentation } from './components/Documentation';
import { ConnectionModal } from './components/ConnectionModal';
import { AboutModal } from './components/AboutModal';
import { TabPicker } from './components/TabPicker';
import { TabAddButton } from './components/TabAddButton';
import { EmptyWatermark } from './components/EmptyWatermark';
import { DeviceStatusIndicator } from './components/DeviceStatusIndicator';
import { useSerialStore } from './stores/serialStore';
import { useLogStore } from './stores/logStore';
import { serialService } from './services/serialService';
import { TAB_REGISTRY } from './constants/tabRegistry';
import { EXTERNAL_URL_BASE } from './constants/config';

// Disconnected view component
const DisconnectedView: React.FC = () => {
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <DeviceStatusIndicator />
      <Button
        variant="contained"
        onClick={() => {
          // Get the App component's connect handler
          const event = new CustomEvent('openConnectionModal');
          window.dispatchEvent(event);
        }}
      >
        Connect
      </Button>
    </Box>
  );
};

// Higher-order component to wrap panels with connection check
const withConnectionCheck = (Component: React.FC, panelId: string) => {
  return () => {
    const isConnected = useSerialStore((state) => state.isConnected);
    const tabDef = TAB_REGISTRY.find(tab => tab.id === panelId);
    const allowOnDisconnected = tabDef?.allowOnDisconnected ?? false;

    if (!isConnected && !allowOnDisconnected) {
      return (
        <DisconnectedView />
      );
    }

    return <Component />;
  };
};

// Base component implementations
const FirmwareUpdateBase = () => {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1 }}>
      <FirmwareUpdate />
    </Box>
  );
};

const PCRSettingsBase = () => {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1, overflow: 'auto' }}>
      <PCRSettings />
    </Box>
  );
};

const LogDisplayBase = () => {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1, overflow: 'auto' }}>
      <LogDisplay />
    </Box>
  );
};

const CurrentProtocolPlotBase = () => {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1 }}>
      <CurrentProtocolPlot />
    </Box>
  );
};

const TemperaturePlotBase = () => {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1 }}>
      <TemperaturePlot />
    </Box>
  );
};

const HardwareMonitorPlotBase = () => {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1 }}>
      <HardwareMonitorPlot />
    </Box>
  );
};

const SerialIOBase = () => {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1 }}>
      <SerialIO />
    </Box>
  );
};

const DocumentationBase = () => {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Documentation />
    </Box>
  );
};

// Wrapped components with connection check
const FirmwareUpdatePanel = withConnectionCheck(FirmwareUpdateBase, 'firmwareUpdate');
const PCRSettingsPanel = withConnectionCheck(PCRSettingsBase, 'pcrSettings');
const LogDisplayPanel = withConnectionCheck(LogDisplayBase, 'logDisplay');
const CurrentProtocolPlotPanel = withConnectionCheck(CurrentProtocolPlotBase, 'currentProtocolPlot');
const TemperaturePlotPanel = withConnectionCheck(TemperaturePlotBase, 'temperaturePlot');
const HardwareMonitorPlotPanel = withConnectionCheck(HardwareMonitorPlotBase, 'hardwareMonitorPlot');
const SerialIOPanel = withConnectionCheck(SerialIOBase, 'serialIO');
const DocumentationPanel = withConnectionCheck(DocumentationBase, 'documentation');

// Component registry
const components = {
  firmwareUpdate: FirmwareUpdatePanel,
  pcrSettings: PCRSettingsPanel,
  logDisplay: LogDisplayPanel,
  currentProtocolPlot: CurrentProtocolPlotPanel,
  temperaturePlot: TemperaturePlotPanel,
  hardwareMonitorPlot: HardwareMonitorPlotPanel,
  serialIO: SerialIOPanel,
  documentation: DocumentationPanel,
};

interface LayoutData {
  layout: SerializedDockview;
}

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    // Load dark mode from its own localStorage key
    try {
      const saved = localStorage.getItem('dark-mode');
      if (saved !== null) {
        return saved === 'true';
      }
    } catch (error) {
      console.error('Failed to load dark mode:', error);
    }
    return true; // Default to dark mode
  });
  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const [tabPickerOpen, setTabPickerOpen] = useState(false);
  const [connectionModalOpen, setConnectionModalOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [wantedConnectionState, setWantedConnectionState] = useState(true); // true = want to be connected
  const [skipAutoConnect, setSkipAutoConnect] = useState(false);
  const [targetGroup, setTargetGroup] = useState<DockviewGroupPanel | null>(null);
  const [isSmallScreen, setIsSmallScreen] = useState(() => {
    return window.innerWidth < 960 || window.innerHeight < 1400;
  });
  const dockviewRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<DockviewApi | null>(null);
  const isInitialSetupRef = useRef(true); // Track if we're in initial setup

  const theme = useMemo(() => getTheme(darkMode ? 'dark' : 'light'), [darkMode]);

  const { isConnected, statusColor, port, setPort, setIsConnected, setStatus } = useSerialStore();
  const addLog = useLogStore((state) => state.addLog);

  // Get port name for display
  const getPortName = () => {
    if (!port) return '';
    const info = port.getInfo();
    // Try to get a readable name, fallback to vendor/product IDs
    if (info.usbVendorId && info.usbProductId) {
      return `USB ${info.usbVendorId.toString(16).toUpperCase()}:${info.usbProductId.toString(16).toUpperCase()}`;
    }
    return 'Serial Port';
  };

  // Handle external links - open in system browser if in Electron
  const handleExternalLink = (url: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    // Check if running in Electron
    const electronAPI = (window as any).electronAPI;
    if (electronAPI?.isElectron) {
      // Use IPC to open in system browser
      electronAPI.openExternal(url);
    } else {
      // In browser, just open normally
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Reset skipAutoConnect when connection is established
  useEffect(() => {
    if (isConnected) {
      setSkipAutoConnect(false);
      setConnectionModalOpen(false);
    }
  }, [isConnected]);

  // Listen for custom event to open connection modal from disconnected panels
  useEffect(() => {
    const handleOpenConnectionModal = () => {
      handleConnectClick();
    };
    window.addEventListener('openConnectionModal', handleOpenConnectionModal);
    return () => window.removeEventListener('openConnectionModal', handleOpenConnectionModal);
  }, []);

  // Listen for unexpected device disconnections
  useEffect(() => {
    const unsubscribe = serialService.onDisconnect(() => {
      setPort(null);
      setIsConnected(false);
      setStatus('Device disconnected', 'error');
      addLog('Device disconnected unexpectedly', 'error');
    });

    return unsubscribe;
  }, [setPort, setIsConnected, setStatus, addLog]);

  const handleConnectClick = () => {
    setWantedConnectionState(true);
    setSkipAutoConnect(true);
    setConnectionModalOpen(true);
  };

  // Get the appropriate localStorage key based on screen size
  const getLayoutKey = () => {
    return isSmallScreen ? 'dockview-layout-small' : 'dockview-layout-large';
  };

  const toggleDarkMode = () => {
    setDarkMode((prev) => {
      const newValue = !prev;
      // Save dark mode setting to its own localStorage key
      try {
        localStorage.setItem('dark-mode', String(newValue));
      } catch (error) {
        console.error('Failed to save dark mode:', error);
      }
      return newValue;
    });
  };

  // Detect screen size changes and reload layout if size category changes
  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen((prevIsSmallScreen) => {
        const isSmall = window.innerWidth < 960 || window.innerHeight < 1400;

        // Only proceed if screen size category actually changed
        if (isSmall === prevIsSmallScreen) {
          return prevIsSmallScreen;
        }

        // Reload layout when screen size category changes
        if (apiRef.current) {
          // Mark as initial setup to prevent saving during transition
          isInitialSetupRef.current = true;

          // Clear current layout
          apiRef.current.clear();

          // Small delay to allow clear to complete
          setTimeout(() => {
            if (!apiRef.current) return;

            // Load layout for new screen size
            const key = isSmall ? 'dockview-layout-small' : 'dockview-layout-large';
            const savedLayout = localStorage.getItem(key);

            if (savedLayout) {
              try {
                const data: LayoutData = JSON.parse(savedLayout);
                apiRef.current.fromJSON(data.layout);
              } catch (error) {
                console.error('Failed to restore layout:', error);
                if (isSmall) {
                  createSmallScreenLayout(apiRef.current);
                } else {
                  createDefaultLayout(apiRef.current);
                }
              }
            } else {
              if (isSmall) {
                createSmallScreenLayout(apiRef.current);
              } else {
                createDefaultLayout(apiRef.current);
              }
            }

            // Mark setup as complete after a delay
            setTimeout(() => {
              isInitialSetupRef.current = false;
            }, 100);
          }, 50);
        }

        return isSmall;
      });
    };

    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []); // Empty dependency array - checkScreenSize uses functional setState to avoid stale closures

  // Header actions component wrapper to pass the setTabPickerOpen function
  const HeaderActionsComponent = (props: IDockviewHeaderActionsProps) => {
    return <TabAddButton {...props} onAddClick={() => {
      setTargetGroup(props.group);
      setTabPickerOpen(true);
    }} />;
  };

  // Watermark component wrapper
  const WatermarkComponent = () => {
    return <EmptyWatermark
      onAddPanel={() => {
        setTargetGroup(null);
        setTabPickerOpen(true);
      }}
      onResetLayout={handleResetLayout}
    />;
  };

  // Keyboard shortcut to open tab picker (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        setTabPickerOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleDisconnect = async () => {
    try {
      // User wants to be disconnected
      setWantedConnectionState(false);

      await serialService.disconnect();
      setPort(null);
      setIsConnected(false);
      setStatus('Not connected', 'error');
      addLog('Disconnected from serial port', 'info');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      addLog(`Disconnect error: ${message}`, 'error');
    }
  };

  const createSmallScreenLayout = (api: DockviewApi) => {
    // Create a single group with all panels as tabs
    const group = api.addGroup();

    // Add all panels to the same group, only first is active
    const docPanel = api.addPanel({
      id: 'documentation',
      component: 'documentation',
      title: 'Documentation',
      position: { referenceGroup: group },
      inactive: false,
    });

    api.addPanel({
      id: 'currentProtocolPlot',
      component: 'currentProtocolPlot',
      title: 'Current Protocol',
      position: { referenceGroup: group },
      inactive: true,
    });

    api.addPanel({
      id: 'temperaturePlot',
      component: 'temperaturePlot',
      title: 'Temperatures',
      position: { referenceGroup: group },
      inactive: true,
    });

    api.addPanel({
      id: 'serialIO',
      component: 'serialIO',
      title: 'Serial I/O',
      position: { referenceGroup: group },
      inactive: true,
    });

    api.addPanel({
      id: 'pcrSettings',
      component: 'pcrSettings',
      title: 'PCR Settings',
      position: { referenceGroup: group },
      inactive: true,
    });

    api.addPanel({
      id: 'logDisplay',
      component: 'logDisplay',
      title: 'Log',
      position: { referenceGroup: group },
      inactive: true,
    });

    api.addPanel({
      id: 'firmwareUpdate',
      component: 'firmwareUpdate',
      title: 'Firmware Update',
      position: { referenceGroup: group },
      inactive: true,
    });

    docPanel.focus();
  };

  const createDefaultLayout = (api: DockviewApi) => {
    // Create main left column group
    const leftColumnGroup = api.addGroup();

    // Top: Documentation, Temperature Plot (60s) and Protocol Plot as tabs
    // Add all panels as inactive first
    const rightGroup = api.addPanel({
      id: 'documentation',
      component: 'documentation',
      title: 'Documentation',
      position: { referenceGroup: leftColumnGroup },
      inactive: false,
    });

    const currentProtocolPlot = api.addPanel({
      id: 'currentProtocolPlot',
      component: 'currentProtocolPlot',
      title: 'Current Protocol',
      params: {},
      position: { referenceGroup: rightGroup.group },
      inactive: true,
    });

    const temperaturePlot = api.addPanel({
      id: 'temperaturePlot',
      component: 'temperaturePlot',
      title: 'Temperatures',
      params: {},
      position: { referenceGroup: rightGroup.group },
      inactive: true,
    });

    const serialIO = api.addPanel({
      id: 'serialIO',
      component: 'serialIO',
      title: 'Serial I/O',
      params: {},
      position: { referenceGroup: rightGroup.group },
      inactive: true,
    });

    // Bottom: PCR Settings, Log, and Firmware Update as tabs
    const settingsPanel = api.addPanel({
      id: 'pcrSettings',
      component: 'pcrSettings',
      title: 'PCR Settings',
      position: { referenceGroup: rightGroup.group, direction: 'below' },
      inactive: true,
    });

    const logDisplay = api.addPanel({
      id: 'logDisplay',
      component: 'logDisplay',
      title: 'Log',
      params: {},
      position: { referenceGroup: settingsPanel.group },
      inactive: true,
    });

    const firmwareUpdate = api.addPanel({
      id: 'firmwareUpdate',
      component: 'firmwareUpdate',
      title: 'Firmware Update',
      params: {},
      position: { referenceGroup: settingsPanel.group },
      inactive: true,
    });

    firmwareUpdate.focus();
    logDisplay.focus();
    settingsPanel.focus();
    serialIO.focus();
    temperaturePlot.focus();
    currentProtocolPlot.focus();
    rightGroup.focus();

    // Set initial sizes
    if (leftColumnGroup && rightGroup.group) {
      leftColumnGroup.api.setSize({ width: 480 });
    }
  };

  const handleResetLayout = () => {
    if (!apiRef.current) return;

    // Remove the appropriate layout key
    localStorage.removeItem(getLayoutKey());

    // Clear current layout
    apiRef.current.clear();

    // Recreate default layout based on screen size
    if (isSmallScreen) {
      createSmallScreenLayout(apiRef.current);
    } else {
      createDefaultLayout(apiRef.current);
    }
  };

  const onReady = (event: DockviewReadyEvent) => {
    apiRef.current = event.api;

    // Save layout function - only save if not in initial setup
    const saveLayout = () => {
      if (!apiRef.current || isInitialSetupRef.current) return;
      try {
        // Re-calculate screen size on every save
        const isSmall = window.innerWidth < 960 || window.innerHeight < 1400;
        const key = isSmall ? 'dockview-layout-small' : 'dockview-layout-large';
        const layout = apiRef.current.toJSON();
        const data: LayoutData = { layout };
        localStorage.setItem(key, JSON.stringify(data));
      } catch (error) {
        console.error('Failed to save layout:', error);
      }
    };

    // Listen to layout change events
    event.api.onDidLayoutChange(saveLayout);
    event.api.onDidAddPanel(saveLayout);
    event.api.onDidRemovePanel(saveLayout);
    event.api.onDidActivePanelChange(saveLayout);

    // Try to load saved layout from localStorage
    // Re-check screen size at load time to ensure we use the current layout key
    const loadIsSmall = window.innerWidth < 960 || window.innerHeight < 1400;
    const loadLayoutKey = loadIsSmall ? 'dockview-layout-small' : 'dockview-layout-large';
    const savedLayout = localStorage.getItem(loadLayoutKey);

    if (savedLayout) {
      try {
        const data: LayoutData = JSON.parse(savedLayout);
        event.api.fromJSON(data.layout);
        // Mark that we've loaded a saved layout, so future changes should be saved
        isInitialSetupRef.current = false;

        // No need to check for empty panels - watermark will handle it
        return;
      } catch (error) {
        console.error('Failed to restore layout:', error);
        // Fall through to create default layout
      }
    }

    // Create default layout based on current screen size
    if (loadIsSmall) {
      createSmallScreenLayout(event.api);
    } else {
      createDefaultLayout(event.api);
    }

    // After creating default layout, mark initial setup as complete
    // Use setTimeout to ensure all setup events have fired
    setTimeout(() => {
      isInitialSetupRef.current = false;
    }, 100);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box sx={{
          py: 1,
          px: 3,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          minHeight: '56px',
          flexWrap: 'nowrap',
        }}>
          {/* Hamburger menu - only visible on narrow screens */}
          <IconButton
            onClick={(e) => setMenuAnchorEl(e.currentTarget)}
            sx={{
              display: { xs: 'flex', md: 'none' },
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo/Icon and App name - clickable */}
          <Box
            component="a"
            href="https://easypcr.io"
            onClick={handleExternalLink('https://easypcr.io')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            <Box
              component="img"
              src="./favicon.png"
              alt="EasyPCR"
              sx={{ height: 40, width: 'auto' }}
            />
            <Typography variant="h6" component="h1" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
              EasyPCR
            </Typography>
          </Box>

          {/* Desktop navigation links - hidden on narrow screens */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
            {/* Reset Layout link */}
            <Box
              component="a"
              onClick={handleResetLayout}
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Reset Layout
            </Box>

            {/* Downloads link */}
            <Box
              component="a"
              href={ `${EXTERNAL_URL_BASE}/downloads` }
              onClick={handleExternalLink(`${EXTERNAL_URL_BASE}/downloads`)}
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Downloads
            </Box>

            {/* About link */}
            <Box
              component="a"
              onClick={() => setAboutModalOpen(true)}
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              About
            </Box>
          </Box>

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Reset Layout - visible on narrow screens only */}
          <Box
            component="a"
            onClick={handleResetLayout}
            sx={{
              display: { xs: 'flex', md: 'none' },
              color: 'primary.main',
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Reset Layout
          </Box>

          {/* Icon buttons - hidden on very narrow screens */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.5 }}>
            {/* Connect/Disconnect button */}
            <Tooltip title={isConnected ? `Disconnect from ${getPortName()}` : 'Connect'}>
              <IconButton
                onClick={isConnected ? handleDisconnect : handleConnectClick}
                color="inherit"
                size="small"
              >
                <UsbOffIcon />
              </IconButton>
            </Tooltip>

            {/* Status indicator */}
            <Tooltip title={isConnected ? getPortName() : 'Not connected'}>
              <IconButton
                size="small"
                sx={{
                  cursor: 'help',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {statusColor === 'success' ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <ErrorIcon color="error" />
                )}
              </IconButton>
            </Tooltip>

            {/* Dark mode toggle */}
            <Tooltip title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
              <IconButton onClick={toggleDarkMode} color="inherit" size="small">
                {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Navigation Menu for narrow screens */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={() => setMenuAnchorEl(null)}
        >
          <MenuItem onClick={(e) => {
            handleExternalLink(`${EXTERNAL_URL_BASE}/downloads`)(e);
            setMenuAnchorEl(null);
          }}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Downloads</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            setAboutModalOpen(true);
            setMenuAnchorEl(null);
          }}>
            <ListItemIcon>
              <InfoIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>About</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (isConnected) {
                handleDisconnect();
              } else {
                handleConnectClick();
              }
              setMenuAnchorEl(null);
            }}
            sx={{ display: { xs: 'flex', sm: 'none' } }}
          >
            <ListItemIcon>
              <UsbOffIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{isConnected ? `Disconnect from ${getPortName()}` : 'Connect'}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            toggleDarkMode();
            setMenuAnchorEl(null);
          }}
            sx={{ display: { xs: 'flex', sm: 'none' } }}
          >
            <ListItemIcon>
              {darkMode ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{darkMode ? 'Light Mode' : 'Dark Mode'}</ListItemText>
          </MenuItem>
        </Menu>

        {/* Main content area (below header) */}
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
          {/* Connection Modal */}
          <ConnectionModal
            open={connectionModalOpen || (wantedConnectionState && !isConnected && !connectionModalOpen)}
            onClose={() => {
              setConnectionModalOpen(false);
              setWantedConnectionState(false);
            }}
            skipAutoConnect={skipAutoConnect || connectionModalOpen}
          />

          {/* About Modal */}
          <AboutModal open={aboutModalOpen} onClose={() => setAboutModalOpen(false)} />

          {/* Tab Picker Modal */}
          <TabPicker
            open={tabPickerOpen}
            onClose={() => setTabPickerOpen(false)}
            api={apiRef.current}
            availableTabs={TAB_REGISTRY}
            targetGroup={targetGroup}
          />

          {/* Dockview Container */}
          <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
            <DockviewReact
              ref={dockviewRef}
              onReady={onReady}
              components={components}
              className={darkMode ? 'dockview-theme-dark' : 'dockview-theme-light'}
              leftHeaderActionsComponent={HeaderActionsComponent}
              watermarkComponent={WatermarkComponent}
            />
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
